import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ApiFilters,
  Pagination,
  SortDirection,
  StatePageSettings,
} from '@core/core.types';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { catchError, of, switchMap, tap, throwError } from 'rxjs';
import { PriceRequestService } from '../services/price-request.service';
import {
  PriceRequest,
  PriceRequestList,
  PriceRequestStatus,
} from '../types/pricing.types';
import {
  ApprovePriceRequestsAction,
  DeletePriceRequestAction,
  LoadPriceRequestsListAction,
} from './price-request.actions';

const PRICE_REQUEST_STATE_NAME = 'priceRequests';

export interface PriceRequestStateModel {
  priceRequests: { [id: string]: PriceRequest };
  pages?: {
    [key: string]: {
      items: string[];
    };
  };
  currentPageSettings: StatePageSettings<PriceRequestFilters>;
  totalCount?: number;
}

@State<PriceRequestStateModel>({
  name: PRICE_REQUEST_STATE_NAME,
  defaults: {
    priceRequests: {},
    pages: {},
    currentPageSettings: {
      pageIndex: 0,
      sortSettings: {
        orderBy: 'Generated',
        sortDirection: SortDirection.desc,
        pageSize: 30,
      },
      filterSettings: {},
    },
  },
})
@Injectable()
export class PriceRequestState {
  private priceRequestService = inject(PriceRequestService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  @Selector()
  static getCurrentPageIds(state: PriceRequestStateModel) {
    const {
      currentPageSettings: { pageIndex },
      pages,
    } = state;

    if (Object.keys(pages).length === 0 || pageIndex < 0) {
      return [];
    }

    return state.pages[pageIndex].items.slice();
  }

  @Selector([PriceRequestState, PriceRequestState.getCurrentPageIds])
  static getCurrentPage(state: PriceRequestStateModel, ids: string[]) {
    return ids
      .map((id) => state.priceRequests[id])
      .filter((priceRequest) => !!priceRequest);
  }

  @Selector()
  static getPagination({
    currentPageSettings,
    totalCount,
  }: PriceRequestStateModel): Pagination<PriceRequestFilters> {
    return {
      currentPageSettings: {
        ...currentPageSettings,
      },
      totalCount,
      pagesSize: Math.ceil(
        totalCount / currentPageSettings.sortSettings.pageSize
      ),
      collectionSize: currentPageSettings.sortSettings.pageSize,
    };
  }

  @Action(LoadPriceRequestsListAction)
  LoadPriceRequestsListAction(
    { patchState, getState }: StateContext<PriceRequestStateModel>,
    { filters, pageToLoad = 1 }: LoadPriceRequestsListAction
  ) {
    return this.priceRequestService
      .getPriceRequestsList({ ...filters, page: pageToLoad })
      .pipe(
        tap((priceRequests: PriceRequestList) => {
          patchState({
            priceRequests: {
              ...getState().priceRequests,
              ...priceRequests.data.reduce((acc, priceRequest) => {
                acc[priceRequest.id] = priceRequest;
                return acc;
              }, {}),
            },
            totalCount: priceRequests.totalCount,
            pages: {
              ...getState().pages,
              [pageToLoad - 1]: {
                items: priceRequests.data.map(
                  (priceRequest: PriceRequest) => priceRequest.id
                ),
              },
            },
            currentPageSettings: {
              ...getState().currentPageSettings,
              pageSize: filters.limit,
              pageIndex: pageToLoad - 1,
              sortSettings: {
                ...getState().currentPageSettings.sortSettings,
                pageSize: filters.limit,
              },
              filterSettings: { ...filters },
            },
          });
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.Forbidden) {
            this.toastService.danger(
              this.translateService.instant(
                'pricing.errors.missingAccess.message'
              ),
              this.translateService.instant(
                'pricing.errors.missingAccess.title'
              ),
              0
            );
          } else {
            this.toastService.danger(
              error.error?.message,
              error.error?.error,
              0
            );
          }

          patchState({
            priceRequests: {},
            pages: {},
          });

          return throwError(() => error);
        })
      );
  }

  @Action(ApprovePriceRequestsAction)
  ApprovePriceRequestsAction(
    { dispatch, getState }: StateContext<PriceRequestStateModel>,
    { ids }: ApprovePriceRequestsAction
  ) {
    return this.priceRequestService.approvePriceRequests(ids).pipe(
      switchMap(() => dispatch(this.reloadCurrentPage(getState()))),
      catchError((error: HttpErrorResponse) => {
        this.toastService.danger(error.error?.message, error.error?.error, 0);
        return of(null);
      })
    );
  }

  @Action(DeletePriceRequestAction)
  DeletePriceRequestAction(
    { dispatch, getState }: StateContext<PriceRequestStateModel>,
    { priceRequest }: DeletePriceRequestAction
  ) {
    return this.priceRequestService
      .deletePriceRequest(priceRequest.partnerId, priceRequest.id)
      .pipe(
        switchMap(() => dispatch(this.reloadCurrentPage(getState()))),
        catchError((error: HttpErrorResponse) => {
          this.toastService.danger(error.error?.message, error.error?.error, 0);
          return of(null);
        })
      );
  }

  private reloadCurrentPage(state: PriceRequestStateModel) {
    return new LoadPriceRequestsListAction(
      {
        ...(state.currentPageSettings.filterSettings ?? {}),
        limit: state.currentPageSettings.sortSettings.pageSize,
      },
      state.currentPageSettings.pageIndex + 1
    );
  }
}

export interface PriceRequestFilters extends ApiFilters {
  page?: number;
  limit?: number;
  partnerId?: string;
  countries?: string[];
  status?: PriceRequestStatus;
}
